import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Check, X, TrendingUp, Key, RefreshCw, Zap, CalendarClock, Mail, Settings } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/geo`;

export default function GeoCitationWidget() {
  const [stats, setStats] = useState(null);
  const [queries, setQueries] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [testingDigest, setTestingDigest] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [recipientInput, setRecipientInput] = useState('');
  const [dayInput, setDayInput] = useState('mon');
  const [hourInput, setHourInput] = useState(9);

  const load = async () => {
    try {
      const [s, q, sched] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/queries`),
        axios.get(`${API}/schedule`),
      ]);
      setStats(s.data);
      setQueries(q.data.queries || []);
      setSchedule(sched.data);
      setRecipientInput(sched.data.recipient_email || '');
      setDayInput(sched.data.day_of_week || 'mon');
      setHourInput(sched.data.hour ?? 9);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const seed = async () => {
    try {
      const r = await axios.post(`${API}/seed`, { reset: false });
      toast.success(`Seeded ${r.data.inserted} queries (total: ${r.data.total})`);
      await load();
    } catch (e) {
      toast.error('Seed failed');
    }
  };

  const runAll = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/run`, {});
      toast.success(`Tracked ${r.data.run_size} queries — ${Math.round(r.data.our_citation_rate * 100)}% cited us`);
      await load();
    } catch (e) {
      if (e?.response?.status === 503) {
        toast.error('Add PERPLEXITY_API_KEY to backend/.env first');
      } else {
        toast.error(e?.response?.data?.detail || 'Run failed');
      }
    } finally { setRunning(false); }
  };

  const saveSchedule = async (enabled) => {
    if (enabled && !recipientInput.trim()) {
      toast.error('Enter a recipient email first');
      return;
    }
    try {
      const r = await axios.put(`${API}/schedule`, {
        enabled,
        recipient_email: recipientInput.trim() || null,
        day_of_week: dayInput,
        hour: Number(hourInput),
      });
      setSchedule(r.data);
      toast.success(enabled ? 'Weekly digest scheduled' : 'Schedule disabled');
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Save failed');
    }
  };

  const testDigest = async () => {
    if (!recipientInput.trim()) { toast.error('Enter a recipient email first'); return; }
    setTestingDigest(true);
    try {
      // Persist the email first so the digest can use it
      await axios.put(`${API}/schedule`, {
        enabled: schedule?.enabled ?? false,
        recipient_email: recipientInput.trim(),
        day_of_week: dayInput,
        hour: Number(hourInput),
      });
      const r = await axios.post(`${API}/digest/test`, {});
      toast.success(`Digest sent — ${Math.round(r.data.digest.rate_now * 100)}% cited rate`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test digest failed');
    } finally { setTestingDigest(false); }
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-5 text-center text-zinc-500 text-sm">Loading GEO tracker…</CardContent>
      </Card>
    );
  }

  const live = stats?.live_tracking_enabled;
  const rate = stats?.our_citation_rate ?? 0;
  const totalRuns = stats?.total_runs || 0;
  const avgPos = stats?.avg_first_position;

  return (
    <Card className="bg-zinc-900 border-zinc-800" data-testid="geo-citation-widget">
      <CardContent className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="font-medium text-zinc-100">LLM Citation Tracker <span className="text-xs text-zinc-500 font-normal">(GEO)</span></p>
              <p className="text-xs text-zinc-500">How often Perplexity cites getbitsy.ai on hospitality queries</p>
            </div>
          </div>
          <Badge variant="outline" className={live
            ? 'border-emerald-500/40 text-emerald-400'
            : 'border-amber-500/40 text-amber-400'}>
            {live ? 'LIVE' : 'SCAFFOLD'}
          </Badge>
        </div>

        {/* Not-configured banner */}
        {!live && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="geo-setup-banner">
            <Key className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-zinc-100 font-medium">Add PERPLEXITY_API_KEY to enable live tracking</p>
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                Get a key at <a href="https://www.perplexity.ai/settings/api" target="_blank" rel="noreferrer" className="text-amber-400 hover:underline">perplexity.ai/settings/api</a>,
                add it to <code className="bg-zinc-800 px-1 rounded">backend/.env</code>, and restart.
                Until then, the query list and schema are scaffolded but no live calls are made.
              </p>
            </div>
          </div>
        )}

        {/* KPI tiles */}
        <div className="grid grid-cols-3 gap-3">
          <Tile label="Cited rate" value={totalRuns ? `${Math.round(rate * 100)}%` : '—'}
            accent={rate >= 0.6 ? 'text-emerald-400' : rate >= 0.3 ? 'text-amber-400' : 'text-rose-400'}
            hint={totalRuns ? `${stats.our_cited_runs}/${totalRuns} runs` : 'no runs yet'} />
          <Tile label="Avg position" value={avgPos !== null && avgPos !== undefined ? `#${(avgPos + 1).toFixed(1)}` : '—'}
            accent="text-zinc-100" hint="lower is better" />
          <Tile label="Queries tracked" value={queries.length}
            accent="text-zinc-100" hint="editable below" />
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-wrap">
          {queries.length === 0 && (
            <Button size="sm" onClick={seed} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="seed-geo-btn">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Seed 10 default queries
            </Button>
          )}
          {queries.length > 0 && (
            <Button size="sm" onClick={runAll} disabled={running || !live}
              className="bg-amber-500 hover:bg-amber-600 text-black disabled:opacity-50"
              data-testid="run-all-geo-btn">
              <Zap className="w-3.5 h-3.5 mr-1.5" />
              {running ? 'Running…' : `Run all ${queries.length} queries`}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setScheduleOpen((v) => !v)}
            className={`border-zinc-700 text-zinc-300 ${schedule?.enabled ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-400' : ''}`}
            data-testid="toggle-schedule-btn">
            <CalendarClock className="w-3.5 h-3.5 mr-1.5" />
            {schedule?.enabled ? `Auto-run: ${schedule.day_of_week} ${String(schedule.hour).padStart(2,'0')}:00 UTC` : 'Weekly auto-run'}
          </Button>
          <Button size="sm" variant="outline" onClick={load}
            className="border-zinc-700 text-zinc-300" data-testid="refresh-geo-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* Schedule panel */}
        {scheduleOpen && (
          <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-4 space-y-3" data-testid="geo-schedule-panel">
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4 text-amber-400" />
              <p className="text-sm font-medium">Weekly auto-run digest</p>
            </div>
            <p className="text-xs text-zinc-500 leading-relaxed">
              Every week at the time below, the tracker runs all queries and emails a week-over-week
              delta (cited rate, flipped queries, top competitor domains). Requires
              {schedule?.live_tracking_enabled ? <> PERPLEXITY_API_KEY <Check className="inline w-3 h-3 text-emerald-400" /></> : <> <b>PERPLEXITY_API_KEY</b> <X className="inline w-3 h-3 text-rose-400" /></>}
              &nbsp;and
              {schedule?.email_configured ? <> BREVO_API_KEY <Check className="inline w-3 h-3 text-emerald-400" /></> : <> <b>BREVO_API_KEY</b> <X className="inline w-3 h-3 text-rose-400" /></>}.
            </p>
            <div className="grid grid-cols-[1fr_120px_100px] gap-2">
              <div>
                <label className="text-[10px] tracking-widest text-zinc-500">RECIPIENT EMAIL</label>
                <div className="flex items-center gap-2 mt-1">
                  <Mail className="w-3.5 h-3.5 text-zinc-500" />
                  <Input
                    type="email"
                    value={recipientInput}
                    onChange={(e) => setRecipientInput(e.target.value)}
                    placeholder="you@hotel.com"
                    className="bg-zinc-900 border-zinc-700 text-sm h-8"
                    data-testid="digest-recipient-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-widest text-zinc-500">DAY</label>
                <select value={dayInput} onChange={(e) => setDayInput(e.target.value)}
                  className="w-full mt-1 h-8 bg-zinc-900 border border-zinc-700 text-sm rounded px-2" data-testid="digest-day-select">
                  {['mon','tue','wed','thu','fri','sat','sun'].map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] tracking-widest text-zinc-500">HOUR UTC</label>
                <Input
                  type="number"
                  min={0}
                  max={23}
                  value={hourInput}
                  onChange={(e) => setHourInput(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-sm h-8 mt-1"
                  data-testid="digest-hour-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              {schedule?.enabled ? (
                <Button size="sm" variant="outline" onClick={() => saveSchedule(false)}
                  className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10" data-testid="disable-schedule-btn">
                  Disable
                </Button>
              ) : (
                <Button size="sm" onClick={() => saveSchedule(true)}
                  className="bg-emerald-500 hover:bg-emerald-600 text-black" data-testid="enable-schedule-btn">
                  Enable weekly digest
                </Button>
              )}
              <Button size="sm" variant="outline" disabled={testingDigest || !live}
                onClick={testDigest} className="border-amber-500/40 text-amber-400" data-testid="test-digest-btn">
                <Mail className="w-3.5 h-3.5 mr-1.5" />
                {testingDigest ? 'Sending…' : 'Send test digest now'}
              </Button>
            </div>
          </div>
        )}

        {/* Query list */}
        {queries.length > 0 && (
          <div className="border-t border-zinc-800 pt-4">
            <p className="text-xs tracking-widest text-zinc-500 mb-2">TRACKED QUERIES</p>
            <div className="space-y-1.5">
              {queries.slice(0, 10).map((q) => {
                const last = q.last_result;
                return (
                  <div key={q.query_id}
                    className="flex items-center gap-3 p-2.5 rounded-lg bg-zinc-950/50 border border-zinc-800/50 text-xs"
                    data-testid={`geo-query-${q.query_id}`}>
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      {!last && <span className="text-zinc-600">—</span>}
                      {last && last.our_cited && <Check className="w-4 h-4 text-emerald-400" />}
                      {last && !last.our_cited && <X className="w-4 h-4 text-rose-400" />}
                    </div>
                    <span className="text-zinc-200 flex-1 truncate">{q.text}</span>
                    {last?.our_first_position !== null && last?.our_first_position !== undefined && (
                      <span className="text-[10px] text-emerald-400 tabular-nums">#{last.our_first_position + 1}</span>
                    )}
                    {last && (
                      <span className="text-[10px] text-zinc-500 tabular-nums">{last.total_citations} refs</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Top competitors — only show once we have data */}
        {stats?.top_competitor_domains?.length > 0 && (
          <div className="border-t border-zinc-800 pt-4">
            <p className="text-xs tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
              <TrendingUp className="w-3 h-3" /> TOP COMPETITOR DOMAINS (BY CITATIONS)
            </p>
            <div className="flex flex-wrap gap-1.5">
              {stats.top_competitor_domains.slice(0, 8).map((c) => (
                <span key={c.domain} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700">
                  {c.domain} <span className="text-zinc-500">×{c.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Tile({ label, value, accent, hint }) {
  return (
    <div className="rounded-lg bg-zinc-950/50 border border-zinc-800/50 p-3">
      <p className="text-[10px] tracking-widest text-zinc-500">{label.toUpperCase()}</p>
      <p className={`text-2xl font-semibold tabular-nums mt-1 ${accent}`}>{value}</p>
      <p className="text-[10px] text-zinc-500 mt-0.5">{hint}</p>
    </div>
  );
}
